import { useCallback, useEffect, useId, useState, type FormEvent } from 'react'
import { api, ApiError } from '@/lib/apiClient'
import { Button } from '@/components/ui/Button'
import type { CompanyCoupon } from '@/lib/coupons'

type Kind = 'INITIAL' | 'RENEWAL' | 'EXPANSION'

/** "te queda 1 pago", "te quedan 2 pagos". */
function remaining(count: number) {
  return `te ${count === 1 ? 'queda' : 'quedan'} ${count.toLocaleString('es-MX')} ${count === 1 ? 'pago' : 'pagos'}`
}

/**
 * The discount coupon of a purchase: the one the company redeemed, or a field to redeem the code it was mailed.
 * Reports the percentage the purchase takes, zero when none; the server applies it when the checkout starts.
 */
export function CheckoutCoupon({ companyId, environmentId, kind, automatic, disabled, onChange }: {
  companyId: string; environmentId: string; kind: Kind; automatic: boolean; disabled?: boolean; onChange: (percentOff: number) => void
}) {
  const id = useId()
  const [coupon, setCoupon] = useState<CompanyCoupon | null>(null)
  const [hidden, setHidden] = useState(false)
  const [code, setCode] = useState('')
  const [redeeming, setRedeeming] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const current = await api.get<{ coupon: CompanyCoupon | null }>(`/companies/${companyId}/coupon`, { environmentId, kind })
      setCoupon(current.coupon); setHidden(false)
    } catch (cause) {
      // A member who manages equipment does not pay; nothing to show.
      setCoupon(null); setHidden(cause instanceof ApiError && cause.status === 403)
    }
  }, [companyId, environmentId, kind])

  useEffect(() => { void load() }, [load])
  useEffect(() => { onChange(coupon?.applies ? coupon.percentOff : 0) }, [coupon, onChange])

  async function redeem(event: FormEvent) {
    event.preventDefault()
    if (!code.trim()) { setError('Escribe el código de tu cupón.'); return }
    setRedeeming(true); setError('')
    try {
      await api.post(`/companies/${companyId}/coupon`, { code: code.trim() })
      setCode('')
      await load()
    } catch (cause) { setError(cause instanceof ApiError ? cause.message : 'No pudimos canjear el cupón.') } finally { setRedeeming(false) }
  }

  if (hidden) return null
  if (coupon) {
    const left = coupon.periodsLeft
    return <div className="checkout-coupon" aria-live="polite">
      <strong>Cupón {coupon.code}</strong>
      {!coupon.applies ? <p className="field-hint">No aplica a esta compra{left > 0 ? `; ${remaining(left)} con ${coupon.percentOff} % de descuento.` : '.'}</p>
        : kind === 'EXPANSION' ? <p className="field-hint">{coupon.percentOff} % de descuento: tu periodo actual se pagó con este cupón, y lo que agregas lleva el mismo descuento.</p>
        : automatic ? <p className="field-hint">{coupon.percentOff} % de descuento en este pago{left === 2 ? ' y en el siguiente cargo automático' : left > 2 ? ` y en los ${(left - 1).toLocaleString('es-MX')} cargos automáticos siguientes` : ''}.</p>
        : <p className="field-hint">{coupon.percentOff} % de descuento en este pago{left > 1 ? `; después ${remaining(left - 1)} con descuento` : ''}.</p>}
    </div>
  }
  return <form noValidate className="checkout-coupon checkout-coupon-redeem" onSubmit={(event) => void redeem(event)}>
    <div className="form-field">
      <label htmlFor={`${id}-code`}>Cupón de descuento</label>
      <input id={`${id}-code`} value={code} autoComplete="off" autoCapitalize="characters" spellCheck={false} placeholder="BS-XXXX-XXXX" disabled={disabled || redeeming}
        aria-invalid={error ? true : undefined} aria-describedby={`${id}-hint`} onChange={(event) => { setCode(event.target.value); setError('') }} />
      <p id={`${id}-hint`} className={error ? 'field-error' : 'field-hint'} role={error ? 'alert' : undefined}>{error || 'Si te enviamos un cupón por correo, escríbelo aquí.'}</p>
    </div>
    <Button type="submit" emphasis="outline" busy={redeeming} disabled={disabled}>Aplicar</Button>
  </form>
}
